<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent class="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <ShieldCheck class="w-5 h-5 text-amber-600" />
          Admin Edit
        </DialogTitle>
        <DialogDescription>
          Edit application data without updating the
          <code class="text-xs bg-gray-100 px-1 py-0.5 rounded">updated_at</code> timestamp.
        </DialogDescription>
      </DialogHeader>

      <!-- Basic info -->
      <div class="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3 text-sm">
        <div class="flex items-center gap-2 min-w-0">
          <User class="w-4 h-4 text-gray-400 shrink-0" />
          <span class="font-medium truncate">{{ applicantName }}</span>
        </div>
        <span class="text-gray-300">|</span>
        <div class="flex items-center gap-2 min-w-0">
          <Building2 class="w-4 h-4 text-gray-400 shrink-0" />
          <span class="truncate">{{ companyName }}</span>
        </div>
        <span class="text-gray-300">|</span>
        <div class="flex items-center gap-2 min-w-0">
          <Briefcase class="w-4 h-4 text-gray-400 shrink-0" />
          <span class="truncate">{{ roleName }}</span>
        </div>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-5">
        <!-- Section: Statuses -->
        <fieldset class="space-y-3">
          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <Label for="application_status">Application Status</Label>
              <Select v-model="form.application_status">
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="opt in applicationStatusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-1.5">
              <Label for="interview_status">Interview Status</Label>
              <Select v-model="form.interview_status">
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="opt in interviewStatusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-1.5">
              <Label for="referral_type">Referral Type</Label>
              <Select v-model="form.referral_type">
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="opt in referralTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-1.5">
              <Label for="referral_status">Referral Status</Label>
              <Select v-model="form.referral_status">
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="opt in referralStatusOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-1.5">
              <Label for="referral_request_email_sent">Referral Request Email Sent</Label>
              <Select v-model="referralEmailSentString">
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </fieldset>

        <hr class="border-gray-200" />

        <!-- Section: Match / Referral Data -->
        <fieldset class="space-y-3">
          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <Label for="match_status">Match Status</Label>
              <Select v-model="form.match_status">
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__empty__">(empty)</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-1.5">
              <Label for="match_score">Match Score</Label>
              <Input
                id="match_score"
                type="number"
                step="0.01"
                min="0"
                max="100"
                v-model="form.match_score"
                placeholder="e.g. 7.5"
              />
            </div>
          </div>

          <div class="space-y-1.5">
            <Label for="match_assessment">Match Assessment</Label>
            <Textarea id="match_assessment" v-model="form.match_assessment" rows="2" />
          </div>

          <div class="space-y-1.5">
            <Label for="match_error_message">Match Error Message</Label>
            <Textarea id="match_error_message" v-model="form.match_error_message" rows="2" />
          </div>

          <div class="space-y-1.5">
            <Label for="match_key_points">Match Key Points (JSON)</Label>
            <Textarea
              id="match_key_points"
              v-model="form.match_key_points_json"
              rows="4"
              placeholder='{"key_alignments": [...], "key_gaps": [...]}'
              class="font-mono text-xs"
            />
            <p v-if="keyPointsJsonError" class="text-xs text-red-500">{{ keyPointsJsonError }}</p>
          </div>
        </fieldset>

        <hr class="border-gray-200" />

        <!-- Section: Comments -->
        <fieldset class="space-y-3">
          <div class="space-y-1.5">
            <Label for="comments">Comments</Label>
            <Textarea id="comments" v-model="form.comments" rows="2" />
          </div>

          <div class="space-y-1.5">
            <Label for="internal_comments">Internal Comments</Label>
            <Textarea id="internal_comments" v-model="form.internal_comments" rows="2" />
          </div>
        </fieldset>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            @click="handleOpenChange(false)"
            :disabled="saving"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :disabled="saving"
            class="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Loader2 v-if="saving" class="w-4 h-4 mr-2 animate-spin" />
            Save Changes
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  ShieldCheck,
  Loader2,
  User,
  Building2,
  Briefcase
} from 'lucide-vue-next'
import { useApplicationsStore } from '@/stores/applications'
import { useToast } from '@/components/ui/toast'
import type { Application } from '@/types/application'
import {
  applicationStatusOptions,
  interviewStatusOptions,
  referralTypeOptions,
  referralStatusOptions
} from '@/const/applicationStatuses'

const props = defineProps<{
  open: boolean
  application: Application
  applicationId: string
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  saved: [updatedApplication: Application]
}>()

const applicationsStore = useApplicationsStore()
const { toast } = useToast()
const saving = ref(false)
const keyPointsJsonError = ref('')

const applicantName = computed(() => {
  const u = props.application.user_details
  if (!u) return 'Unknown'
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || 'Unknown'
})

const companyName = computed(() => {
  return props.application.open_role_details?.employer_details?.name || '—'
})

const roleName = computed(() => {
  return props.application.open_role_details?.role_name || '—'
})

interface FormData {
  application_status: string
  interview_status: string
  referral_type: string
  referral_status: string
  comments: string
  internal_comments: string
  referral_request_email_sent: boolean
  match_score: string
  match_assessment: string
  match_key_points_json: string
  match_status: string
  match_error_message: string
}

const createFormFromApplication = (app: Application): FormData => ({
  application_status: app.application_status || 'NOT_STARTED',
  interview_status: app.interview_status || 'NOT_STARTED',
  referral_type: app.referral_type || 'NO_REFERRAL',
  referral_status: app.referral_status || 'NOT_STARTED',
  comments: app.comments || '',
  internal_comments: app.internal_comments || '',
  referral_request_email_sent: !!(app as any).referral_request_email_sent,
  match_score: app.match_score != null ? String(app.match_score) : '',
  match_assessment: app.match_assessment || '',
  match_key_points_json: app.match_key_points ? JSON.stringify(app.match_key_points, null, 2) : '',
  match_status: app.match_status || '__empty__',
  match_error_message: app.match_error_message || ''
})

const form = ref<FormData>(createFormFromApplication(props.application))

const referralEmailSentString = computed({
  get: () => (form.value.referral_request_email_sent ? 'true' : 'false'),
  set: (val: string) => {
    form.value.referral_request_email_sent = val === 'true'
  }
})

watch(
  () => props.application,
  (newApp) => {
    form.value = createFormFromApplication(newApp)
  }
)

const handleOpenChange = (value: boolean) => {
  emit('update:open', value)
}

// eslint-disable-next-line complexity
const buildPatchPayload = (): Record<string, any> | null => {
  const payload: Record<string, any> = {}
  const original = props.application
  const f = form.value

  if (f.application_status !== (original.application_status || 'NOT_STARTED')) {
    payload.application_status = f.application_status
  }
  if (f.interview_status !== (original.interview_status || 'NOT_STARTED')) {
    payload.interview_status = f.interview_status
  }
  if (f.referral_type !== (original.referral_type || 'NO_REFERRAL')) {
    payload.referral_type = f.referral_type
  }
  if (f.referral_status !== (original.referral_status || 'NOT_STARTED')) {
    payload.referral_status = f.referral_status
  }
  if (f.comments !== (original.comments || '')) {
    payload.comments = f.comments || null
  }
  if (f.internal_comments !== (original.internal_comments || '')) {
    payload.internal_comments = f.internal_comments || null
  }

  if (f.referral_request_email_sent !== !!(original as any).referral_request_email_sent) {
    payload.referral_request_email_sent = f.referral_request_email_sent
  }

  const formScore = f.match_score !== '' ? parseFloat(f.match_score) : null
  const origScore = original.match_score ?? null
  if (formScore !== origScore) {
    payload.match_score = formScore
  }

  if (f.match_assessment !== (original.match_assessment || '')) {
    payload.match_assessment = f.match_assessment || null
  }

  if (f.match_error_message !== (original.match_error_message || '')) {
    payload.match_error_message = f.match_error_message || null
  }

  const formMatchStatus = f.match_status === '__empty__' ? null : f.match_status
  const origMatchStatus = original.match_status ?? null
  if (formMatchStatus !== origMatchStatus) {
    payload.match_status = formMatchStatus
  }

  const origKeyPoints = original.match_key_points
    ? JSON.stringify(original.match_key_points, null, 2)
    : ''
  if (f.match_key_points_json !== origKeyPoints) {
    if (!f.match_key_points_json.trim()) {
      payload.match_key_points = null
    } else {
      try {
        payload.match_key_points = JSON.parse(f.match_key_points_json)
        keyPointsJsonError.value = ''
      } catch {
        keyPointsJsonError.value = 'Invalid JSON'
        return null
      }
    }
  }

  return payload
}

const handleSubmit = async () => {
  const payload = buildPatchPayload()

  if (payload === null) return

  if (Object.keys(payload).length === 0) {
    toast({ title: 'No changes', description: 'No fields were modified.' })
    return
  }

  saving.value = true
  try {
    const updated = await applicationsStore.guideEditApplication(props.applicationId, payload)
    toast({
      title: 'Saved',
      description: 'Application updated without changing the updated_at timestamp.'
    })
    emit('saved', updated)
    handleOpenChange(false)
  } catch (error: any) {
    let errorMessage = 'Failed to save changes. Please try again.'

    if (error?.response?.data) {
      const data = error.response.data
      const messages: string[] = []
      for (const key in data) {
        if (Array.isArray(data[key])) {
          messages.push(`${key}: ${data[key].join(', ')}`)
        } else if (typeof data[key] === 'string') {
          messages.push(data[key])
        }
      }
      if (messages.length) errorMessage = messages.join('\n')
    }

    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive',
      duration: 10000
    })
  } finally {
    saving.value = false
  }
}
</script>
